// Iter65 Addition 2 + 4 — Master Admin Notifications Widget
// Live aggregator card showing new applications by role + pending orders.
// Polls /api/admin/notifications/summary every 30 seconds. Surfaces alerts
// the admin needs to action.
import React, { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { Bell, Users, Store, Truck, Building2, ShoppingCart, AlertCircle, Loader2 } from "lucide-react";
import api, { apiErrorMessage } from "@/lib/api";

const POLL_MS = 30_000;

function Stat({ icon: Icon, label, value, to, accent, testid }) {
  const cls = accent && value > 0
    ? "border-amber-400/50 hover:border-amber-300 text-amber-200"
    : "border-white/10 hover:border-lifted-green/50 text-white";
  const content = (
    <div className={`glass-panel p-3 border ${cls} transition`} data-testid={testid}>
      <div className="flex items-center justify-between gap-2">
        <Icon size={14} className="text-lifted-dim shrink-0" />
        <span className="font-anton text-2xl">{value}</span>
      </div>
      <div className="text-[10px] uppercase tracking-[0.2em] font-bebas text-lifted-dim mt-1">
        {label}
      </div>
    </div>
  );
  return to ? <Link to={to} className="block">{content}</Link> : content;
}

export default function AdminNotificationsSummary() {
  const [data, setData] = useState(null);
  const [err, setErr] = useState("");
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    let cancelled = false;
    const load = async () => {
      try {
        const { data } = await api.get("/admin/notifications/summary");
        if (!cancelled) {
          setData(data);
          setErr("");
        }
      } catch (e) {
        if (!cancelled) setErr(apiErrorMessage(e));
      } finally {
        if (!cancelled) setLoading(false);
      }
    };
    load();
    const t = setInterval(load, POLL_MS);
    return () => { cancelled = true; clearInterval(t); };
  }, []);

  if (loading) {
    return (
      <section className="glass-panel p-5" data-testid="admin-notifications-loading">
        <div className="flex items-center gap-2 text-xs text-lifted-dim">
          <Loader2 className="animate-spin" size={12} /> Loading activity…
        </div>
      </section>
    );
  }
  if (err) {
    return (
      <section className="glass-panel p-5" data-testid="admin-notifications-error">
        <div className="text-xs text-rose-300 flex items-center gap-2">
          <AlertCircle size={12} /> {err}
        </div>
      </section>
    );
  }
  if (!data) return null;

  const totalAlerts = data.total_alerts || 0;

  return (
    <section
      className={`glass-panel p-5 ${totalAlerts > 0 ? "border-amber-400/40" : ""}`}
      data-testid="admin-notifications-summary"
    >
      <header className="flex items-center justify-between mb-3">
        <div className="flex items-center gap-2">
          <Bell className={totalAlerts > 0 ? "text-amber-300" : "text-lifted-dim"} size={16} />
          <h2 className="font-anton text-xl uppercase text-white">Live activity</h2>
          {totalAlerts > 0 && (
            <span
              className="inline-flex items-center justify-center min-w-[1.5rem] px-2 h-5 text-[10px] font-anton rounded-full bg-amber-400 text-black"
              data-testid="admin-notifications-total"
            >
              {totalAlerts}
            </span>
          )}
        </div>
        <span className="text-[10px] font-mono text-lifted-dim">refreshes every 30s</span>
      </header>

      <div className="grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-6 gap-3">
        <Stat
          icon={Store}
          label="New partner apps"
          value={data.applications.new_partner}
          accent
          to="/admin/applications?role=partner&status=new"
          testid="notif-partner-apps"
        />
        <Stat
          icon={Truck}
          label="New driver apps"
          value={data.applications.new_driver}
          accent
          to="/admin/applications?role=driver&status=new"
          testid="notif-driver-apps"
        />
        <Stat
          icon={Building2}
          label="New franchise apps"
          value={data.applications.new_franchisee}
          accent
          to="/admin/applications?role=franchisee&status=new"
          testid="notif-franchise-apps"
        />
        <Stat
          icon={ShoppingCart}
          label="New orders"
          value={data.orders.new}
          accent
          to="/admin/orders?status=created"
          testid="notif-new-orders"
        />
        <Stat
          icon={ShoppingCart}
          label="Pending orders"
          value={data.orders.pending}
          to="/admin/orders"
          testid="notif-pending-orders"
        />
        <Stat
          icon={Users}
          label="Live users"
          value={data.ecosystem.users_live}
          to="/admin/users"
          testid="notif-live-users"
        />
      </div>
    </section>
  );
}
